import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Ship, PipetteIcon as Pipeline, Anchor, Zap, ArrowRight } from 'lucide-react';

const Services = () => {
  const { t } = useTranslation();

  const services = [
    {
      id: 'marine-construction',
      icon: Ship,
      title: t('home.services.marine.title'),
      desc: t('home.services.marine.desc'),
      color: 'blue',
      image: 'https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/axckgrya_liman%20hizmetleri.png'
    },
    {
      id: 'pipeline-piping',
      icon: Pipeline,
      title: t('home.services.pipeline.title'),
      desc: t('home.services.pipeline.desc'),
      badge: t('home.services.pipeline.badge'),
      featured: true,
      color: 'amber',
      image: 'https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/wh3xnodo_magic_edit%23TUFITFhWRVp0V2sjMSNhODZkNGE0YWNhNzM1ZmEzMjc5YjlkZmFhZDA3MjE3NiMxMjgwIyNUUkFOU0ZPUk1BVElPTl9SRVFVRVNU.jpg'
    },
    {
      id: 'offshore-operations',
      icon: Anchor,
      title: t('home.services.offshore.title'),
      desc: t('home.services.offshore.desc'),
      color: 'cyan',
      image: 'https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/8wjmm32s_Offshor%20works%20jpg.jpeg'
    },
    {
      id: 'energy-infrastructure',
      icon: Zap,
      title: t('home.services.energy.title'),
      desc: t('home.services.energy.desc'),
      color: 'orange',
      image: 'https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/jgyl5xvl_LNG%20to%20Power.jpeg'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="services-title">
              {t('services.title')}
            </h1>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              {t('services.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`relative rounded-xl overflow-hidden transition-all hover:shadow-2xl group ${
                    service.featured 
                      ? 'ring-4 ring-amber-400'
                      : 'shadow-lg'
                  }`}
                >
                  {/* Image Header */}
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className={`absolute inset-0 ${
                      service.featured 
                        ? 'bg-gradient-to-t from-blue-900/95 via-blue-900/50 to-transparent'
                        : 'bg-gradient-to-t from-slate-900/80 via-slate-900/30 to-transparent'
                    }`}></div>
                    {service.badge && (
                      <span className="absolute top-4 right-4 px-3 py-1 bg-amber-400 text-slate-900 text-xs font-bold rounded-full shadow-lg">
                        {service.badge}
                      </span>
                    )}
                    <Icon className="absolute top-4 left-4 text-white drop-shadow-lg" size={40} />
                  </div>

                  {/* Content */}
                  <div className={`p-8 ${
                    service.featured 
                      ? 'bg-gradient-to-br from-blue-900 to-slate-900 text-white'
                      : 'bg-white'
                  }`}>
                    <h3 className={`text-2xl font-bold mb-4 ${service.featured ? 'text-white' : 'text-slate-900'}`}>
                      {service.title}
                    </h3>
                    <p className={`mb-6 text-lg ${service.featured ? 'text-blue-100' : 'text-gray-600'}`}>
                      {service.desc}
                    </p>
                    <Link
                      to={`/services/${service.id}`}
                      className={`inline-flex items-center font-semibold text-lg ${
                        service.featured 
                          ? 'text-amber-400 hover:text-amber-300'
                          : 'text-blue-900 hover:text-blue-700'
                      }`}
                    data-testid={`service-link-${service.id}`}
                  >
                    {t('services.learn')} <ArrowRight className="ml-2" size={20} />
                  </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            {t('home.cta.title')}
          </h2>
          <p className="text-xl text-blue-200 mb-8">
            {t('home.cta.subtitle')}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-all transform hover:scale-105"
            data-testid="services-cta-contact"
          >
            {t('services.cta')} <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;